
import React from "react";
import { motion } from "framer-motion";
import { Layout } from "@/components/Layout";
import { Link, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <Layout className="py-10 px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-3xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground">Effective Date: April 4, 2025</p>
        </div>
        
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p>
            Fatato Pottery Studio ("we", "us", or "our") is committed to protecting your privacy. 
            This Privacy Policy outlines how we collect, use, disclose, and safeguard your information 
            when you visit our website <a href="https://fatato.am/" target="_blank" rel="noopener noreferrer" 
            className="text-primary hover:underline inline-flex items-center">
              https://fatato.am/ <Link className="h-3.5 w-3.5 ml-1" />
            </a> (the "Site") and use our services, including registration and class booking functionalities. 
            By using our Site, you agree to the collection and use of information in accordance with this policy.
          </p>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">1. Information We Collect</h2>
          <p>
            We collect personal information from you when you engage with our Site, such as when you register for an account, 
            book classes, or contact us. This information may include:
          </p>
          <ul>
            <li className="mb-2"><strong>Personal Identification Information:</strong> Name, email address, phone number, and mailing address.</li>
            <li className="mb-2"><strong>Account Information:</strong> Username, password, and other details necessary to access your account.</li>
            <li className="mb-2"><strong>Usage Data:</strong> Information about your interactions with our Site, such as IP address, browser type, pages visited, and the time and date of your visit.</li>
          </ul>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">2. How We Use Your Information</h2>
          <p>We use the information we collect for various purposes, including:</p>
          <ul>
            <li className="mb-2">To create and manage your account.</li>
            <li className="mb-2">To process and manage your class bookings.</li>
            <li className="mb-2">To communicate with you regarding your account, bookings, and our services.</li>
            <li className="mb-2">To improve our Site and services by analyzing user behavior.</li>
            <li className="mb-2">To send promotional emails and offers, if you have opted to receive them.</li>
          </ul>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">3. Sharing Your Information</h2>
          <p>
            We do not sell, trade, or rent your personal identification information to others. 
            We may share your information with third parties in the following situations:
          </p>
          <ul>
            <li className="mb-2"><strong>Service Providers:</strong> We may share your information with third-party vendors who perform services on our behalf, such as email delivery. These service providers are contractually obligated to protect your information.</li>
            <li className="mb-2"><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
          </ul>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">4. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the 
            Internet or method of electronic storage is 100% secure.
          </p>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">5. Your Rights</h2>
          <p>
            You have the right to access, correct, or delete your personal information. You can do this by logging into your 
            account or contacting us directly. Additionally, you may opt-out of receiving promotional communications from us 
            by following the unsubscribe instructions provided in those communications.
          </p>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">6. Third-Party Links</h2>
          <p>
            Our Site may contain links to third-party websites. We are not responsible for the privacy practices or content 
            of these external sites. We encourage you to read the privacy policies of any linked websites you visit.
          </p>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">7. Changes to This Privacy Policy</h2>
          <p>
            We reserve the right to update this Privacy Policy at any time. We will notify you of any changes by posting 
            the new policy on our Site with a new effective date. You are advised to review this Privacy Policy periodically 
            for any changes.
          </p>

          <h2 className="text-2xl font-display font-bold mt-8 mb-4">8. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our practices, please contact us at:
          </p>
          <p className="flex items-center mt-4">
            <Mail className="h-5 w-5 mr-2 text-primary" />
            <span className="font-medium">Fatato Pottery Studio</span>
          </p>
          <p className="flex items-center">
            <Link className="h-5 w-5 mr-2 text-primary" />
            <a href="https://fatato.am/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              https://fatato.am/
            </a>
          </p>
        </div>
      </motion.div>
    </Layout>
  );
};

export default PrivacyPolicy;
